import { Link } from "react-router-dom";
import { Mail, ShieldCheck, Music2, Instagram, Facebook, Youtube } from "lucide-react";

const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Footer({ content }) {
  const brand = content?.brand_name ?? "Vowyo";
  const legal = content?.legal_pages?.length ? content.legal_pages : [];
  const blurb = content?.footer_blurb ?? "Real songs, written from your story and recorded by real artists. The gift that gets happy tears.";
  const email = content?.footer_email ?? "hello@vowyo.com";
  const goodToKnow = (content?.footer_good_to_know ?? "Secure Checkout\nYour story stays private\nFree corrections on delivery")
    .split("\n").map((s) => s.trim()).filter(Boolean);
  const creditText = content?.footer_credit_text ?? "21PixelStudio";
  const creditUrl = content?.footer_credit_url ?? "https://21pixelstudio.com/";
  const copyright = content?.copyright_text ?? `© 2026 ${brand}. All rights reserved.`;
  const socials = [
    { key: "instagram", url: content?.social_instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", url: content?.social_facebook, Icon: Facebook, label: "Facebook" },
    { key: "x", url: content?.social_x, Icon: XIcon, label: "X" },
    { key: "youtube", url: content?.social_youtube, Icon: Youtube, label: "YouTube" },
  ].filter((s) => s.url);

  return (
    <footer className="border-t-[3px] border-ink bg-ink text-cream" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-[1.2fr_1fr_1fr] gap-10">
          <div>
            <img src="/logo.png" alt={brand} className="h-9 w-auto bg-cream rounded-xl px-3 py-1.5" />
            <p className="mt-4 text-sm text-cream/70 font-medium max-w-xs leading-relaxed" data-testid="footer-blurb">
              {blurb}
            </p>
            {email && (
              <a href={`mailto:${email}`} data-testid="footer-email-link" className="mt-4 inline-flex items-center gap-2 text-sm font-head font-semibold text-sunny hover:text-pink transition-colors">
                <Mail size={14} /> {email}
              </a>
            )}
            {socials.length > 0 && (
              <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
                {socials.map(({ key, url, Icon, label }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${key}`}
                    className="btn-pop inline-flex items-center justify-center w-10 h-10 rounded-full bg-cream/10 border-2 border-cream/25 text-cream/80 hover:bg-pink hover:border-cream hover:text-white transition-colors"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="font-display font-semibold text-sm uppercase tracking-wider text-sunny mb-4">Quick Links</p>
            <ul className="space-y-2.5 text-sm font-medium">
              <li><Link to="/track" data-testid="footer-track-link" className="text-cream/70 hover:text-pink transition-colors inline-flex items-center gap-1.5"><Music2 size={12} /> Track Your Song</Link></li>
              {legal.map((p) => (
                <li key={p.slug}>
                  <Link to={`/legal/${p.slug}`} data-testid={`footer-legal-${p.slug}`} className="text-cream/70 hover:text-pink transition-colors">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-display font-semibold text-sm uppercase tracking-wider text-sunny mb-4">Good to know</p>
            <ul className="space-y-2.5 text-sm text-cream/70 font-medium" data-testid="footer-good-to-know">
              {goodToKnow.map((item, i) => (
                <li key={i} className="flex items-center gap-2"><ShieldCheck size={14} className="text-mint shrink-0" /> {item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-cream/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[11px] text-cream/50" data-testid="footer-copyright">{copyright}</p>
          {creditText && (
            <a href={creditUrl} target="_blank" rel="noreferrer" data-testid="footer-credit-link"
              className="font-mono text-[11px] text-cream/50 hover:text-sunny transition-colors">
              Crafted by <span className="underline underline-offset-4">{creditText}</span>
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
